/**
 * useActionHandlers Hook
 *
 * Handles action execution logic including predefined and custom actions.
 */

import { useCallback } from "react";
import type { DetailAction, BaseEntity, DetailActionContext } from "../types/detailPage";
import { executeDetailAction } from "../actions/execute-action";

interface UseActionHandlersProps<T extends BaseEntity> {
  item: T;
  actionContext: DetailActionContext<T>;
  onSetLoadingAction: (actionId: string, isLoading: boolean) => void;
}

interface UseActionHandlersReturn<T extends BaseEntity> {
  handleAction: (action: DetailAction<T>) => Promise<void>;
}

/**
 * Hook for handling action execution
 *
 * @param item - Current entity item
 * @param actionContext - Action context from provider
 * @param onSetLoadingAction - Callback to set loading state
 * @returns Action handlers
 */
export function useActionHandlers<T extends BaseEntity>({
  item,
  actionContext,
  onSetLoadingAction,
}: UseActionHandlersProps<T>): UseActionHandlersReturn<T> {
  // Handle action execution
  const handleAction = useCallback(
    async (action: DetailAction<T>) => {
      if (
        action.disabled &&
        typeof action.disabled === "function" &&
        action.disabled(item)
      ) {
        return;
      }

      onSetLoadingAction(action.id, true);

      try {
        await executeDetailAction(action, actionContext);
      } finally {
        onSetLoadingAction(action.id, false);
      }
    },
    [actionContext, onSetLoadingAction]
  );

  return {
    handleAction,
  };
}
